import { Button, Card } from "@ui/shad"
import { Download, Eye, FileText, Loader2, Pencil } from "lucide-react"

import { useActionLink } from "@/hooks/api/action-links"
import { useDocumentEditorDialog } from "@/components/document-editor/use-document-editor-dialog"

interface DownloadDocumentProps {
  id: string
}

const preparingStatuses = new Set(["pending", "extracting", "rendering", "normalizing", "building"])

export default function DownloadDocument({ id }: DownloadDocumentProps) {
  const { data: actionLink, isLoading } = useActionLink(id, {
    refetchInterval: (query) => {
      const status = query.state.data?.deliverable_status
      return status && preparingStatuses.has(status) ? 2_000 : false
    },
  })
  const openDocumentEditorDialog = useDocumentEditorDialog()
  const deliverableId = actionLink?.deliverable_id
  const filename =
    typeof actionLink?.properties?.filename === "string" ? actionLink.properties.filename : "document.pdf"

  const isPreparing = !!actionLink?.deliverable_status && preparingStatuses.has(actionLink.deliverable_status)

  if (isLoading || isPreparing) {
    return (
      <Card className="mt-2 flex w-full max-w-md items-center gap-3 p-3 shadow-sm">
        <span className="bg-tinted text-icon flex size-9 shrink-0 items-center justify-center rounded-md">
          <Loader2 className="size-4 animate-spin" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">Preparing PDF...</p>
          <p className="text-muted-foreground text-xs">This will be ready in a moment</p>
        </div>
      </Card>
    )
  }

  if (!actionLink) return null

  if (actionLink.deliverable_status === "failed") {
    return (
      <Card className="mt-2 flex w-full max-w-md items-center gap-3 p-3 shadow-sm">
        <span className="bg-tinted text-icon flex size-9 shrink-0 items-center justify-center rounded-md">
          <FileText className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">Couldn&apos;t prepare PDF</p>
          <p className="text-muted-foreground text-xs">Ask in chat to try again</p>
        </div>
      </Card>
    )
  }

  const storedDownloadUrl =
    typeof actionLink.properties?.download_url === "string" ? actionLink.properties.download_url : undefined
  const storedPreviewUrl =
    typeof actionLink.properties?.preview_url === "string" ? actionLink.properties.preview_url : undefined
  const downloadUrl = actionLink.download_url ?? storedDownloadUrl
  const previewUrl = actionLink.preview_url ?? storedPreviewUrl ?? downloadUrl

  if (!downloadUrl) return null

  const openEditor = () => {
    if (!deliverableId) return

    openDocumentEditorDialog({ deliverableId, actionLinkId: id, title: filename })
  }

  return (
    <div className="mt-2 flex w-full max-w-md items-center gap-2">
      <Card className="flex min-w-0 flex-1 items-center gap-3 p-3 shadow-sm">
        <span className="bg-tinted text-icon flex size-9 shrink-0 items-center justify-center rounded-md">
          <FileText className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{filename}</p>
          <p className="text-muted-foreground text-xs">
            {filename.toLowerCase().endsWith(".png") ? "PNG image" : "PDF document"}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {previewUrl && (
            <Button asChild variant="ghost" size="sm">
              <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                <Eye />
                Preview
              </a>
            </Button>
          )}
          <Button asChild variant="outline" size="sm">
            <a href={downloadUrl} download={filename} target="_blank" rel="noopener noreferrer">
              <Download />
              Download
            </a>
          </Button>
        </div>
      </Card>
      {deliverableId && (
        <Button variant="ghost" size="sm" onClick={openEditor} className="shrink-0">
          <Pencil />
          Edit
        </Button>
      )}
    </div>
  )
}
